import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, X } from "lucide-react";
import TabsCst from "@components/TabsCst";
import { isClassroomActivity } from "@utils/cardActions";
import { UsersAndGroupsTab, UsersAndGroupsTriggerPayload } from "./types";
import EnrolledUsersTab from "./tabs/EnrolledUsersTab";
import PreEnrolledUsersTab from "./tabs/PreEnrolledUsersTab";
import WaitingUsersTab from "./tabs/WaitingUsersTab";
import EnrolledGroupsTab from "./tabs/EnrolledGroupsTab";

interface UsersAndGroupsModalProps {
  trigger: UsersAndGroupsTriggerPayload;
  onClose: () => void;
}

const TAB_KEYS: UsersAndGroupsTab[] = [
  "enrolled-users",
  "pre-enrolled-users",
  "waiting-users",
  "enrolled-groups",
];

const UsersAndGroupsModal: React.FC<UsersAndGroupsModalProps> = ({
  trigger,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const [activeTab, setActiveTab] = useState<UsersAndGroupsTab>(
    trigger.initialTab,
  );

  const listsBlocked = trigger.isStandalone === false;
  const isTabDisabled = (key: UsersAndGroupsTab) =>
    key === "waiting-users" || key === "pre-enrolled-users"
      ? listsBlocked && !isClassroomActivity(trigger.activityType)
      : listsBlocked;

  const tabs = TAB_KEYS.map((key) => ({
    key,
    label: String(t(`tabs.${key}`, key)),
    disabled: isTabDisabled(key),
  }));

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "20px 24px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.text.primary,
              }}
            >
              <GraduationCap size={16} />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.primary,
                }}
              >
                {trigger.objectTitle || "-"}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TabsCst
            tabs={tabs}
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as UsersAndGroupsTab)}
          />
        </Box>
      </Box>

      <DialogContent
        sx={{
          padding: "16px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        {!isTabDisabled("enrolled-users") && activeTab === "enrolled-users" && (
          <EnrolledUsersTab
            corporateId={trigger.corporateId}
            objectId={trigger.objectId}
            objectTitle={trigger.objectTitle}
            objectType={trigger.objectType}
            activityType={trigger.activityType}
          />
        )}
        {!isTabDisabled("pre-enrolled-users") &&
          activeTab === "pre-enrolled-users" && (
            <PreEnrolledUsersTab
              corporateId={trigger.corporateId}
              objectId={trigger.objectId}
              objectTitle={trigger.objectTitle}
              objectType={trigger.objectType}
            />
          )}
        {!isTabDisabled("waiting-users") && activeTab === "waiting-users" && (
          <WaitingUsersTab
            corporateId={trigger.corporateId}
            objectId={trigger.objectId}
            editionId={trigger.editionId}
          />
        )}
        {!isTabDisabled("enrolled-groups") &&
          activeTab === "enrolled-groups" && (
            <EnrolledGroupsTab
              corporateId={trigger.corporateId}
              objectId={trigger.objectId}
            />
          )}
      </DialogContent>
    </Dialog>
  );
};

export default UsersAndGroupsModal;
